import { ChangeEvent, FormEvent, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { loginUser, loginUserWithLdap } from '../auth/authSlice';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useParseQueryString } from '../traceHistory/hooks/useParseQueryString';
import { makeShareWeatherUrlNoHost } from 'features/weatherReflection/utils/urlManagers';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';

function Copyright() {
  return (
    <Typography variant='body2' color='textSecondary' align='center'>
      {'Copyright © '}
      <Link color='inherit' href='lptech.com' underline='hover'>
        SPECTRAS
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}

export default function SignInSide() {
  const dispatch = useAppDispatch();
  const loggedInUser = useAppSelector((state) => state.auth.user);
  const { loginError } = useAppSelector((state) => state.auth);
  const { timezone } = useUserDateTime();
  const [user, setUser] = useState({
    emailOrUserName: '',
    password: '',
  });

  const {
    hasQueryString,
    startTime,
    endTime,
    snapshotIdToLoad,
    analyzerIdToLoad,
    traceIndex,
    weather,
    forecast,
    weatherConstraints,
    dishIdToLoad,
    redirectPath,
  } = useParseQueryString();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUser({
      ...user,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    try {
      const result = await dispatch(
        loginUser({
          ...user,
          name: user.emailOrUserName,
          email: user.emailOrUserName,
        })
      );
      unwrapResult(result);
    } catch (e) {
      await dispatch(
        loginUserWithLdap({
          username: user.emailOrUserName,
          mail: user.emailOrUserName,
          password: user.password,
        })
      );
    }
  };

  if (loggedInUser) {
    if (hasQueryString) {
      if (weather) {
        if (!startTime || !endTime) return <Navigate to={'/'} />;
        const weatherUrl = makeShareWeatherUrlNoHost(
          new Date(startTime),
          new Date(endTime),
          analyzerIdToLoad,
          snapshotIdToLoad,
          traceIndex,
          weatherConstraints,
          timezone
        );
        return <Navigate to={weatherUrl} />;
      } else if (forecast) {
        if (!startTime || !endTime) return <Navigate to={'/'} />;
        const weatherUrl = makeShareWeatherUrlNoHost(
          new Date(startTime),
          new Date(endTime),
          analyzerIdToLoad,
          snapshotIdToLoad,
          traceIndex,
          weatherConstraints,
          timezone,
          true,
          dishIdToLoad
        );
        return <Navigate to={weatherUrl} />;
      } else if (analyzerIdToLoad && Number(analyzerIdToLoad) !== -1) {
        return (
          <Navigate
            to={`/trace-history?analyzer=${analyzerIdToLoad}&snapshot=${snapshotIdToLoad}&start=${startTime}&end=${endTime}&trace=${traceIndex}`}
          />
        );
      } else if (redirectPath) {
        return <Navigate to={redirectPath} />;
      }
    }
    return <Navigate to={'/'} />;
  }

  return (
    <Grid
      container
      component='main'
      sx={{
        mt: 8,
      }}
      alignItems='center'
      justifyContent='center'
    >
      <Grid item xs={10} sm={8} md={3} component={Paper} elevation={5}>
        <Box
          sx={{
            margin: (theme) => theme.spacing(2, 2),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography component='h1' variant='h4'>
            Spectras
          </Typography>
          <Typography component='h1' variant='h5'>
            Sign in
          </Typography>
          <Box
            component='form'
            sx={{
              width: '100%', // Fix IE 11 issue.
              mt: 1,
            }}
            onSubmit={handleSubmit}
            noValidate
          >
            <TextField
              variant='outlined'
              margin='normal'
              required
              fullWidth
              id='emailOrUserName'
              label='Email Address Or Username'
              name='emailOrUserName'
              autoComplete='email'
              autoFocus
              value={user.emailOrUserName}
              onChange={handleChange}
              error={loginError}
            />
            <TextField
              variant='outlined'
              margin='normal'
              required
              fullWidth
              name='password'
              label='Password'
              type='password'
              id='password'
              autoComplete='current-password'
              value={user.password}
              onChange={handleChange}
              error={loginError}
            />
            {loginError && (
              <h3 style={{ color: 'red' }}>Incorrect Username or Password</h3>
            )}
            <Button
              type='submit'
              fullWidth
              variant='contained'
              color='primary'
              sx={{
                margin: (theme) => theme.spacing(3, 0, 2),
              }}
            >
              Sign In
            </Button>
            <Box mt={5}>
              <Copyright />
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}
